import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Spinner,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { RiAddLine, RiDeleteBinLine } from "react-icons/ri";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import {
  SearchSelect,
  Item as SearchSelectItem,
} from "src/components/Inputs/SearchSelect/SearchSelect";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import * as GQL from "src/types/graphql";
import { BoundaryImpactWarningModal } from "./components/BoundaryImpactWarningModal";

type BoundaryConnectionFSR =
  FSR.GetBoundaryConnectionsBySchoolIdResponse["data"]["connections"][number];

type BoundaryConnectionAction = "CREATE" | "UPDATE" | "DELETE" | "NOOP";

type BoundaryConnection = {
  action: BoundaryConnectionAction;
  connection_ids: string[];
  key: string;
  boundary_id: string | null;
  school_id: string | null;
  grade_config_ids: string[] | null;
  label: string | null;
  is_inside: boolean;
};

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  enrollmentPeriodId: string;
  schoolId: string;
  schoolName: string;
  grades: GQL.GetEnrollmentSchoolsAndConfigs_school_grades[];
  refetchBoundaryMap?: () => Promise<void>;
}

type GradeConfigMultiSelectItem = {
  id: string;
  label: string;
};

export const SetBoundaryConnectionsDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationId,
  enrollmentPeriodId,
  schoolId,
  grades,
  refetchBoundaryMap,
}) => {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const [boundaryConnections, setBoundaryConnections] = useState<
    BoundaryConnection[]
  >([]);

  const [isLoadingBoundaryConnections, setIsLoadingBoundaryConnections] =
    useState<boolean>(false);

  const [boundarySelectItems, setBoundarySelectItems] = useState<
    SearchSelectItem[]
  >([]);
  const [isLoadingSelectItems, setIsLoadingSelectItems] =
    useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const gradeConfigSelectItems: GradeConfigMultiSelectItem[] = Array.from(
    new Map(
      grades.map((x) => [
        x.grade_config.id,
        { id: x.grade_config.id, label: x.grade_config.label },
      ])
    ).values()
  );

  const gradeConfigLookupTable: Map<string, string[]> = new Map();
  for (const g of grades) {
    const ids: string[] = gradeConfigLookupTable.get(g.grade_config.id) ?? [];
    ids.push(g.id);
    gradeConfigLookupTable.set(g.grade_config.id, ids);
  }

  const toast = useAvelaToast();

  const fetchConnections = useCallback(async () => {
    setIsLoadingBoundaryConnections(true);
    try {
      const resp = await FSR.getBoundaryConnectionsBySchoolId({
        path: {
          schoolId,
          enrollmentPeriodId,
        },
      });
      const modelConnections: BoundaryConnectionFSR[] =
        resp.data?.data.connections ?? [];
      const viewModelConnections: BoundaryConnection[] = [];

      for (const conn of modelConnections) {
        const tgt = viewModelConnections.find(
          (x) =>
            x.boundary_id === conn.boundary_id && x.school_id === conn.school_id
        );
        const gradeConfigId =
          conn.grade_id === null
            ? null
            : grades.find((g) => g.id === conn.grade_id)?.grade_config?.id ??
              null;
        if (!tgt) {
          const key = crypto.randomUUID();
          viewModelConnections.push({
            action: "NOOP",
            connection_ids: [conn.id],
            key: key,
            boundary_id: conn.boundary_id ?? null,
            school_id: conn.school_id ?? null,
            grade_config_ids: gradeConfigId ? [gradeConfigId] : null,
            label: conn.label ?? null,
            is_inside: conn.is_inside ?? false,
          });
        } else {
          tgt.connection_ids = [...tgt.connection_ids, conn.id];
          if (gradeConfigId && tgt.grade_config_ids) {
            const gradeConfigIds = [...tgt.grade_config_ids, gradeConfigId];
            tgt.grade_config_ids = _.uniq(gradeConfigIds);
          }
        }
      }
      setBoundaryConnections(viewModelConnections);
    } catch (e: unknown) {
      setErrorMessage(
        e instanceof Error ? e.message : `an unknown error ocurred.`
      );
    } finally {
      setIsLoadingBoundaryConnections(false);
    }
  }, [grades, schoolId, enrollmentPeriodId]);

  const fetchBoundariesMetadata = useCallback(async () => {
    setIsLoadingSelectItems(true);
    try {
      const resp = await FSR.getBoundariesMetadata({
        query: { organization_id: organizationId },
      });
      const boundarySelectItems = (resp.data?.data.boundaries ?? [])
        .filter((b) => b.key && b.name)
        .map((b) => ({
          key: b.id,
          label: b.name ?? "",
        }));
      setBoundarySelectItems(boundarySelectItems);
    } catch (e: unknown) {
      setErrorMessage(
        e instanceof Error ? e.message : `an unknown error ocurred.`
      );
    } finally {
      setIsLoadingSelectItems(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (isOpen) {
      fetchConnections();
      fetchBoundariesMetadata();
    }
  }, [fetchConnections, fetchBoundariesMetadata, isOpen, grades]);

  const onChangeBoundary = (
    selectedItem: SearchSelectItem | null,
    conn: BoundaryConnection
  ) => {
    const newConnections = boundaryConnections.map((x) =>
      x.key === conn.key
        ? {
            ...x,
            action: (x.action === "CREATE"
              ? "CREATE"
              : "UPDATE") as BoundaryConnectionAction,
            boundary_id: selectedItem ? selectedItem.key : null,
          }
        : x
    );
    setBoundaryConnections(newConnections);
  };

  const onChangeGrade = (
    selectedItems: GradeConfigMultiSelectItem[],
    conn: BoundaryConnection
  ) => {
    const newConnections = boundaryConnections.map((x) =>
      x.key === conn.key
        ? {
            ...x,
            action: (x.action === "CREATE"
              ? "CREATE"
              : "UPDATE") as BoundaryConnectionAction,
            grade_config_ids:
              selectedItems.length > 0 ? selectedItems.map((x) => x.id) : null,
          }
        : x
    );
    setBoundaryConnections(newConnections);
  };

  const onEnableLabel = (isChecked: boolean, conn: BoundaryConnection) => {
    const newConnections = boundaryConnections.map((x) =>
      x.key === conn.key
        ? {
            ...x,
            action: (x.action === "CREATE"
              ? "CREATE"
              : "UPDATE") as BoundaryConnectionAction,
            label: isChecked ? "" : null,
          }
        : x
    );
    setBoundaryConnections(newConnections);
  };

  const onChangeLabel = (label: string, conn: BoundaryConnection) => {
    const newConnections = boundaryConnections.map((x) =>
      x.key === conn.key
        ? {
            ...x,
            action: (x.action === "CREATE"
              ? "CREATE"
              : "UPDATE") as BoundaryConnectionAction,
            label: label ? label : null,
          }
        : x
    );
    setBoundaryConnections(newConnections);
  };

  const onClickRemoveBoundary = (conn: BoundaryConnection) => {
    const newConnections = boundaryConnections.map((x) =>
      x.key === conn.key
        ? { ...x, action: "DELETE" as BoundaryConnectionAction }
        : x
    );
    setBoundaryConnections(newConnections);
  };

  const onClickAddBoundary = () => {
    setBoundaryConnections([
      ...boundaryConnections,
      {
        action: "CREATE",
        connection_ids: [],
        key: crypto.randomUUID(),
        boundary_id: null,
        school_id: schoolId,
        grade_config_ids: null,
        label: null,
        is_inside: true,
      },
    ]);
  };

  const createBoundaryConnections = async (
    connections: BoundaryConnection[]
  ) => {
    type CreatePayloadItem =
      FSR.CreateBoundaryConnectionsData["body"]["connections"][number];
    const createPayloadItems: CreatePayloadItem[] = [];
    for (const conn of connections) {
      if (!conn.boundary_id || !conn.school_id) continue;

      for (const gradeConfig of conn.grade_config_ids ?? ["_ANY_GRADE_"]) {
        const gradeIds = gradeConfigLookupTable.get(gradeConfig) ?? [null];
        for (const gradeId of gradeIds) {
          createPayloadItems.push({
            enrollment_period_id: enrollmentPeriodId,
            boundary_id: conn.boundary_id,
            school_id: conn.school_id,
            grade_id: gradeId,
            label: conn.label,
            is_inside: conn.is_inside,
          });
        }
      }
    }

    if (createPayloadItems.length > 0) {
      await FSR.createBoundaryConnections({
        body: {
          connections: createPayloadItems,
        },
      });
    }
  };

  const deleteBoundaryConnections = async (
    connections: BoundaryConnection[]
  ) => {
    const connectionIds: string[] = [];
    for (const conn of connections) {
      connectionIds.push(...conn.connection_ids);
    }

    if (connectionIds.length > 0)
      await FSR.deleteBoundaryConnections({
        body: {
          ids: connectionIds,
        },
      });
  };

  const updateBoundaryConnections = async (
    connections: BoundaryConnection[]
  ) => {
    await deleteBoundaryConnections(connections);

    await createBoundaryConnections(connections);
  };

  const saveBoundaryConnections = async () => {
    try {
      const createConnections = boundaryConnections.filter(
        (x) => x.action === "CREATE"
      );
      await createBoundaryConnections(createConnections);

      const updateConnections = boundaryConnections.filter(
        (x) => x.action === "UPDATE"
      );
      await updateBoundaryConnections(updateConnections);

      const deleteConnections = boundaryConnections.filter(
        (x) => x.action === "DELETE"
      );
      await deleteBoundaryConnections(deleteConnections);

      toast({
        title: "Success",
        description: "Boundary connections updated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (e: unknown) {
      toast({
        title: "Error",
        description: "Failed to update boundary connections",
        status: "error",
        duration: 5000,
      });
    }

    if (refetchBoundaryMap) await refetchBoundaryMap();

    onClose();
  };

  const onClickCancel = async () => {
    setIsLoadingBoundaryConnections(true);
    try {
      setBoundaryConnections([]);
    } catch (e: unknown) {
      setErrorMessage(
        e instanceof Error ? e.message : `an unknown error ocurred.`
      );
      setBoundaryConnections([]);
    } finally {
      setIsLoadingBoundaryConnections(false);
    }
    onClose();
  };

  return (
    <>
      <BoundaryImpactWarningModal
        isOpen={isWarningModalOpen}
        onClose={() => {
          onClickCancel();
          setIsWarningModalOpen(false);
        }}
        onConfirm={async () => {
          await saveBoundaryConnections();
        }}
        isLoading={isLoadingBoundaryConnections || isLoadingSelectItems}
      />
      <ResizableDrawer
        isOpen={isOpen}
        onClose={() => onClickCancel()}
        header="Set boundaries"
        contentProps={{ bg: "white" }}
        footer={
          <HStack spacing={2} w="full">
            <Button
              colorScheme="gray"
              variant="outline"
              flex={1}
              onClick={() => onClickCancel()}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              flex={1}
              onClick={() => setIsWarningModalOpen(true)}
              isLoading={isLoadingBoundaryConnections || isLoadingSelectItems}
              isDisabled={
                boundaryConnections.filter((x) => x.action !== "NOOP")
                  .length === 0 ||
                boundaryConnections.filter((x) => !x.boundary_id).length > 0
              }
            >
              Set boundaries
            </Button>
          </HStack>
        }
      >
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="lg" fontWeight="semibold" color="gray.900" mb={1}>
              Add boundaries
            </Text>
            <Text fontSize="sm" color="gray.600">
              Indicate which grades should be impacted by a boundary
            </Text>
          </Box>

          <Alert status="warning" bg="orange.50" borderRadius="md">
            <AlertIcon color="orange.500" />
            <Text fontSize="sm" color="gray.700">
              Modifying boundaries may impact any live forms and their responses
            </Text>
          </Alert>

          {errorMessage && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Failed to load boundaries. Please try again.
              </Text>
            </Alert>
          )}

          {(isLoadingBoundaryConnections || isLoadingSelectItems) && (
            <Flex justify="center" py={4}>
              <Spinner color="primary.500" />
              <Text ml={2} color="gray.600">
                Loading...
              </Text>
            </Flex>
          )}
          <VStack spacing={4} align="stretch">
            {boundaryConnections
              .filter((x) => x.action !== "DELETE")
              .map((conn) => (
                <Box
                  key={conn.key}
                  p={6}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="white"
                >
                  <VStack spacing={4} align="stretch">
                    <SearchSelect
                      label="Boundary *"
                      items={boundarySelectItems}
                      selectedItem={
                        conn.boundary_id
                          ? boundarySelectItems.find(
                              (b) => b.key === conn.boundary_id
                            ) ?? null
                          : null
                      }
                      onChange={(item) => onChangeBoundary(item, conn)}
                      placeholder="Search boundaries..."
                      error={
                        !conn.boundary_id
                          ? "Please select a boundary"
                          : undefined
                      }
                    />

                    <MultiSelectInput<GradeConfigMultiSelectItem>
                      size="md"
                      label="Grade (optional)"
                      options={gradeConfigSelectItems}
                      initialValues={
                        conn.grade_config_ids
                          ? gradeConfigSelectItems.filter((g) =>
                              conn.grade_config_ids?.includes(g.id)
                            )
                          : []
                      }
                      onChange={(x) => onChangeGrade(x, conn)}
                      placeholder="Search grades..."
                      renderDropdownItem={(item) => (
                        <Flex key={item.id}>{item.label}</Flex>
                      )}
                    />

                    <HStack spacing={3} align="flex-start">
                      <Switch
                        isChecked={conn.label !== null}
                        onChange={({ target }) =>
                          onEnableLabel(target.checked, conn)
                        }
                        mt={1}
                      />
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                          mb={1}
                        >
                          Label
                        </Text>
                        <Text fontSize="sm" color="gray.600" lineHeight="1.4">
                          When enabled, responses matching this combination of
                          grade and boundary will have a family and staff facing
                          label, visible on school selection
                        </Text>
                        {conn.label !== null && (
                          <Input
                            value={conn.label}
                            onChange={({ target }) =>
                              onChangeLabel(target.value, conn)
                            }
                            placeholder="Enter label"
                            size="sm"
                            bg="white"
                            mt={2}
                          />
                        )}
                      </Box>
                    </HStack>

                    <Flex justify="flex-end">
                      <Button
                        leftIcon={<RiDeleteBinLine />}
                        onClick={() => onClickRemoveBoundary(conn)}
                        variant="outline"
                        size="sm"
                        colorScheme="gray"
                        color="gray.700"
                        isDisabled={conn.boundary_id === null}
                      >
                        Remove boundary
                      </Button>
                    </Flex>
                  </VStack>
                </Box>
              ))}

            {!isLoadingBoundaryConnections && (
              <Button
                leftIcon={<RiAddLine />}
                onClick={() => onClickAddBoundary()}
                variant="outline"
                size="md"
                color="gray.700"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                _hover={{ bg: "gray.50" }}
                w="full"
              >
                Set another boundary
              </Button>
            )}
          </VStack>
        </VStack>
      </ResizableDrawer>
    </>
  );
};
